import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { GlobalContext } from "../../../Context/GlobalContext";
import { useGlobalFunction } from "../../../Context/GlobalFunctionContext";
import { capitalized, isNumberOnly, isValidArray } from "../../../Layout/Common/common";
import AutoSuggestField from "../../../Layout/Common/autoSuggestField";
import { DeliveryInstructionReuired } from "../../../Layout/Common/alert/DeliveryInstructionReuired";
import { RequiredFieldErrorMessage } from "../../../Layout/Common/alert/RequiredFieldErrorMessage";
import { PAGES } from "../../../routeConfig";
import { axiosInstance } from "../../../axios/axiosInstance";
import { useAuth } from "../../../Context/AuthContext";
import { BreadCrumb } from "../../../Layout/Common/breadCrumb";
import { useNoteAlert } from "../../../Context/pleaseNote";
import { FormikErrorMessage } from "../../../Layout/Common/fromikErrorMessage";
import { StandaloneSearchBox } from "@react-google-maps/api";
import { CancelButton } from "../../../Layout/Common/cancelButton";
import { showSuccessSnackbar } from "../../../utils/snackbarNotifications";

export const AddNewAddress = ({ id }) => {
  const { countryList, suburbList } = useContext(GlobalContext);
  const { showFlashScreen, showRestrictionAlert } = useNoteAlert();
  const { getEditAddressBook, getMyAddressBook } = useGlobalFunction();
  const [newAddress, setNewAddress] = useState({
    shipTo: "",
    address1: "",
    address2: "",
    address3: "",
    city: "",
    suburb: "",
    state: "",
    postCode: "",
    country: "Australia",
    phone: "",
    attentionTo: "",
    deliveryInst: "",
    addressType: "",
    signRequired: true,
  });
  const [editAddress, setEditAddress] = useState(null);
  const inputsRef = useRef([]);
  const [editId, setEditId] = useState(0);
  const childRequiredRef = useRef();
  const inputGoogleRef = useRef();
  const childSignRef = useRef();
  const { userId } = useAuth();
  const navigate = useNavigate();
  const australiaBounds = {
    north: -10.0,
    south: -44.0,
    east: 154.0,
    west: 113.0,
  };

  const searchBoxOptions = {
    bounds: australiaBounds, // bias to Australia
    strictBounds: false, // allow other countries
  };
  useEffect(() => {
    if (id && id > 0) getEditData(id);
  }, [id]);

  const getEditData = async (sid) => {
    var params = parseInt(sid) ?? 0;
    var response = null;
    if (params > 0) {
      response = await getEditAddressBook(params);
    }
    if (response) {
      setEditAddress(response);
    } else {
      if (id && id > 0) {
        showRestrictionAlert({
          content: `<p class="text-center col-fs-14 fs-15 fw-normal">Address details not found. Please try again later.</p>`,
          title: "Restricted Area",
          showCloseBtn: true,
          icon: "wes-address-home",
          crossButton: false,
          closeBtnFunction: () => {
            navigate(PAGES.MYACCOUNT);
          },
        });
      }
    }
    let address = response || {};
    setNewAddress({
      shipTo: address.shipTo ?? "",
      address1: address.address1 ?? "",
      address2: address.address2 ?? "",
      address3: address.address3 ?? "",
      city: address.city ?? "",
      suburb: address.suburb ?? "",
      state: address.state ?? "",
      postCode: address.postCode ?? "",
      country: capitalized(address.shipCountry ?? "Australia"),
      phone: address.phone ?? "",
      attentionTo: address.attentionTo ?? "",
      deliveryInst: address.deliveryInst ?? "",
      signRequired: address.signRequired ?? true,
    });
    setEditId(address.id ?? 0);
  };
  const handleSubmit = async (values) => {
    try {
      showFlashScreen(true);
      var address = { ...values, id: 0 };
      var params = {
        userId: String(userId),
        address: address,
        id: editId ?? 0,
      };
      var response = await axiosInstance.post("AddressBook/UpdateAddress", params);
      showSuccessSnackbar(editId > 0 ? "Address updated successfully" : "Address added successfully");
      showFlashScreen(false);
      if (response && response.data) {
        navigate(PAGES.MYADDRESSBOOK, { replace: true });
      }
    } catch {
      showFlashScreen(false);
    }
  };

  const checkRequiredField = (isValid, errors) => {
    if (!isValid) {
      childRequiredRef.current.showAlert();
    }
    if (errors["deliveryInst"]) childSignRef.current.showAlert();
  };

  const handleOnChange = (e, setFieldValue) => {
    const fieldName = e.target.name;
    const value = e.target.value.replace(/[^a-z0-9 ]/gi, "");
    setFieldValue(fieldName, value);
  };
  const handleSuburbChange = (setFieldValue, suburb) => {
    if (suburb) {
      setFieldValue("state", suburb.state);
      setFieldValue("suburb", suburb.suburb);
      setFieldValue("postCode", suburb.postCode);

      setNewAddress((prev) => ({
        ...prev,
        state: suburb.state,
        suburb: suburb.suburb,
        postCode: suburb.postCode,
      }));
    }
  };
  const validationSchema = Yup.object().shape({
    shipTo: Yup.string().required("Recipient name is required"),
    address1: Yup.string().required("Address Line 1 is required"),
    // address2: Yup.string().required("Address Line 2 is required"),
    suburb: Yup.string().required("Suburb is required"),
    state: Yup.string().required("State is required"),
    attentionTo: Yup.string().required("Attention Name is required"),
    postCode: Yup.string()
      .required("ZIP code is required")
      .matches(/^\d{3,6}$/, "ZIP code is incorrect"),
    country: Yup.string().required("Country is required"),
    phone: Yup.string().required("Phone number is required"),
    signRequired: Yup.boolean(),
    deliveryInst: Yup.string().test("deliveryInst-required", "Delivery instruction is required", function (value) {
      const { signRequired } = this.parent;
      if (!signRequired && !value) {
        return false;
      }
      return true;
    }),
  });
  const setInuputRefs = (el) => {
    if (el && !inputsRef.current.includes(el)) {
      inputsRef.current.push(el);
    }
  };
  const handleKeyDown = (e, formik) => {
    if (e.key == "Enter" || e.key == "Tab") {
      e.preventDefault();

      const currentIndex = inputsRef.current.indexOf(e.target);
      const fieldName = e.target.name;
      const value = e.target.value;
      const isRequired = e.target.required;

      if (isRequired && !value) {
        formik.setFieldTouched(fieldName, true, true);
        formik.validateField(fieldName);
        return; // stop moving
      }

      const nextInput = inputsRef.current[currentIndex + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const fillAddressFromGoogle = async (setFieldValue) => {
    const place = inputGoogleRef.current.getPlaces()[0];
    if (place.address_components) {
      let address = place.address_components;
      var street_number = address.find((addr) => addr.types.includes("street_number"));
      var route = address.find((addr) => addr.types.includes("route"));

      var suburb = address.find((addr) => addr.types.includes("locality"));
      var state = address.find((addr) => addr.types.includes("administrative_area_level_1"));
      var country = address.find((addr) => addr.types.includes("country"));
      var postCode = address.find((addr) => addr.types.includes("postal_code"));
      let address1 = [];
      if (street_number) address1.push(street_number["long_name"]);
      if (route) address1.push(route["long_name"]);
      let address1Val = isValidArray(address1) ? address1.join(", ") : address[0]["long_name"];
      setFieldValue("address1", address1Val ?? "");
      setFieldValue("suburb", suburb?.long_name ?? "");
      setFieldValue("state", state?.short_name ?? "");
      setFieldValue("postCode", postCode?.long_name ?? "");
      setFieldValue("country", country?.long_name ?? "");
      if (address1Val) return true;
      return false;
    }
  };

  return (
    <Fragment>
      <section className="myAccount-section">
        <div className="container-fluid container-lg">
          <div className="row">
            <div className="col-lg-12">
              <div className="acc-header py-3">
                <div className="myaccount-address myAccount-header-inner">
                  <h1 className="myaccount-title">{id ? "Edit Address" : "Add New Address"}</h1>
                  <div className="order-invoice myFavoriteList">
                    <p>Fill Below Details to Save New address in your list</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-8" key={id}>
              {newAddress && (
                <Formik
                  initialValues={newAddress}
                  enableReinitialize
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ setFieldValue, errors, isValid, touched, values, ...formik }) => (
                    <Form noValidate>
                      <div className="shippChange-address">
                        <h3 className="fs-6 fw-semibold mb-3">Receiver Address</h3>

                        <div className="row">
                          <div className="col-md-6 col-lg-12">
                            <div className="floating-input mb-3">
                              <Field
                                id="shipTo"
                                name="shipTo"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                required={true}
                                onChange={(e) => handleOnChange(e, setFieldValue)}
                                className={`form-control form-input ${
                                  touched.shipTo && errors.shipTo && "error-message"
                                }`}
                                type="text"
                                placeholder=""
                                maxLength={30}
                              />
                              <label htmlFor="shipTo">
                                Company/Receiver Name
                                <span className="gl-form-asterisk">*</span>
                              </label>

                              <ErrorMessage name="shipTo">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                            </div>
                          </div>

                          <div className="col-md-6 col-lg-6">
                            <div className="floating-input mb-2 mb-lg-3">
                              <Field
                                id="address1"
                                name="address1"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                required={true}
                                className={`form-control form-input ${
                                  touched.address1 && errors.address1 && "error-message"
                                }`}
                                type="text"
                                placeholder=""
                                tabIndex={2}
                                onChange={(e) => handleOnChange(e, setFieldValue)}
                                autoComplete="off"
                              />

                              <label htmlFor="address1">Address1</label>
                              <ErrorMessage name="address1">
                                {(msg) => <FormikErrorMessage message={msg} />}
                              </ErrorMessage>
                            </div>
                          </div>

                          <div className="col-md-6 col-lg-6">
                            <div className="floating-input mb-3">
                              <Field
                                id="address2"
                                name="address2"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                className={`form-control form-input`}
                                type="text"
                                placeholder=""
                                onChange={(e) => handleOnChange(e, setFieldValue)}
                              />
                              <label htmlFor="address2">Address2</label>
                              <ErrorMessage name="address2">
                                {(msg) => <FormikErrorMessage message={msg} />}
                              </ErrorMessage>
                            </div>
                          </div>

                          <div className="col-md-6 col-lg-6">
                            <div className="floating-input mb-3">
                              <AutoSuggestField
                                formik={formik}
                                setFieldValue={setFieldValue}
                                formValue={values}
                                suggestions={suburbList}
                                name="suburb"
                                label="Enter Suburb details"
                                callBackFunction={handleSuburbChange}
                                errors={errors.suburb}
                                touched={touched.suburb}
                                required={true}
                              />
                              <ErrorMessage name="suburb">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                            </div>
                          </div>

                          <div className="col-md-6 col-lg-6">
                            <div className="floating-input  mb-3">
                              <Field
                                type="text"
                                id="state"
                                name="state"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                required={true}
                                disabled={true}
                                className={`form-control form-input ${
                                  touched.state && errors.state && "error-message"
                                }`}
                                onChange={(e) => handleOnChange(e, setFieldValue)}
                                placeholder=""
                                style={{
                                  pointerEvents: editAddress ? "none" : "auto",
                                  opacity: editAddress ? "0.5" : "1",
                                }}
                              ></Field>
                              <label htmlFor="state">
                                State
                                <span className="gl-form-asterisk">*</span>
                              </label>
                              <ErrorMessage name="state">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                            </div>
                          </div>

                          <div className="col-md-6 col-lg-6">
                            <div className="floating-input mb-3">
                              <Field
                                id="postCode"
                                name="postCode"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                required={true}
                                className={`form-control form-input ${
                                  touched.postCode && errors.postCode && `error-message`
                                }`}
                                type="text"
                                placeholder=""
                                onChange={(e) => handleOnChange(e, setFieldValue)}
                                maxLength={10}
                                disabled={true}
                                style={{
                                  pointerEvents: editAddress ? "none" : "auto",
                                  opacity: editAddress ? "0.5" : "1",
                                }}
                              />
                              <label htmlFor="postCode">
                                ZIP Code <span className="gl-form-asterisk">*</span>
                              </label>
                              <ErrorMessage name="postCode">
                                {(msg) => <FormikErrorMessage message={msg} />}
                              </ErrorMessage>
                            </div>
                          </div>
                          {/* <div className="col-md-6 col-lg-6">
                            <div
                              className="choose-state mb-3"
                              style={{
                                pointerEvents: editAddress ? "none" : "auto",
                                opacity: editAddress ? "0.5" : "1",
                              }}
                            >
                              <label htmlFor="state">
                                Country
                                <span className="gl-form-asterisk">*</span>
                              </label>

                              <Field
                                as="select"
                                id="country"
                                name="country"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                required={true}
                                className={`form-select ${touched.state && errors.state ? "error-message" : ""}`}
                                aria-label="Choose State"
                              >
                                {countryList &&
                                  countryList.map((item, index) => (
                                    <option value={capitalized(item.country)}>{item.country}</option>
                                  ))}
                              </Field>

                              <ErrorMessage name="country">
                                {(msg) => <FormikErrorMessage message={msg} />}
                              </ErrorMessage>
                            </div>
                          </div> */}
                          <div className="col-md-6 col-lg-6">
                            <div className="floating-input  mb-3">
                              <Field
                                type="text"
                                id="country"
                                name="country"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                required={true}
                                disabled={true}
                                className={`form-control form-input ${
                                  touched.country && errors.country && "error-message"
                                }`}
                                onChange={(e) => handleOnChange(e, setFieldValue)}
                                placeholder=""
                                style={{
                                  pointerEvents: editAddress ? "none" : "auto",
                                  opacity: editAddress ? "0.5" : "1",
                                }}
                              ></Field>
                              <label htmlFor="country">
                                Country
                                <span className="gl-form-asterisk">*</span>
                              </label>
                              <ErrorMessage name="country">
                                {(msg) => <FormikErrorMessage message={msg} />}
                              </ErrorMessage>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="receiver-details">
                        <h3 className="fs-6 fw-semibold mb-3">Receiver Details</h3>
                        <div className="row">
                          <div className="col-md-6 col-lg-6">
                            <div className="floating-input mb-3">
                              <Field
                                id="phone"
                                name="phone"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                required={true}
                                className={`form-control form-input ${
                                  touched.phone && errors.phone && "error-message"
                                }`}
                                type="text"
                                placeholder=""
                                onChange={(e) => {
                                  setFieldValue("phone", isNumberOnly(e.target.value));
                                }}
                              />
                              <label htmlFor="phone">
                                Phone Number
                                <span className="gl-form-asterisk">*</span>
                              </label>
                              <ErrorMessage name="phone">{(msg) => <FormikErrorMessage message={msg} />}</ErrorMessage>
                            </div>
                          </div>
                          <div className="col-md-6 col-lg-6">
                            <div className="floating-input mb-3">
                              <Field
                                id="attentionTo"
                                name="attentionTo"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                required={true}
                                className={`form-control form-input ${
                                  touched.attentionTo && errors.attentionTo && "error-message"
                                }`}
                                onChange={(e) => handleOnChange(e, setFieldValue)}
                                type="text"
                                placeholder=""
                                maxLength={30}
                              />
                              <label htmlFor="attentionTo">
                                Attention of Name
                                <span className="gl-form-asterisk">*</span>
                              </label>
                              <ErrorMessage name="attentionTo">
                                {(msg) => <FormikErrorMessage message={msg} />}
                              </ErrorMessage>
                            </div>
                          </div>

                          <div className="col-md-6 col-lg-12">
                            <div className="floating-input mb-3">
                              <Field
                                id="deliveryInst"
                                name="deliveryInst"
                                innerRef={setInuputRefs}
                                onKeyDown={(e) => handleKeyDown(e, formik)}
                                className={`form-control form-input`}
                                required={!values.signRequired}
                                type="text"
                                placeholder=""
                                onChange={(e) => handleOnChange(e, setFieldValue)}
                                maxLength={30}
                              />
                              <label htmlFor="deliveryInst">
                                Delivery Instruction{" "}
                                {!values.signRequired && <span className="gl-form-asterisk">*</span>}
                              </label>
                              <ErrorMessage name="deliveryInst">
                                {(msg) => <FormikErrorMessage message={msg} />}
                              </ErrorMessage>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="signature">
                        <h3 className="fs-6 fw-semibold mb-3">Signature Required</h3>
                        <div className="row row-equal">
                          <div className="col-sm-6 mb-3 mb-sm-0">
                            <div className="form-check custom-check signature-req mb-3 mb-md-0">
                              <div className="signature-req-input">
                                <Field
                                  className="form-check-input"
                                  type="radio"
                                  name="signRequired"
                                  innerRef={setInuputRefs}
                                  onKeyDown={(e) => handleKeyDown(e, formik)}
                                  id="signatureIsRequired"
                                  value={true}
                                  checked={values.signRequired == true}
                                  onChange={() => setFieldValue("signRequired", true)}
                                />
                              </div>
                              <label className="form-check-label" htmlFor="signatureIsRequired">
                                Yes, Required
                                <span>Someone will be required to sign for parcel.</span>
                              </label>
                            </div>
                          </div>
                          <div className="col-lg-6">
                            <div className="form-check custom-check signature-req">
                              <div className="signature-req-input">
                                <Field
                                  className="form-check-input"
                                  type="radio"
                                  name="signRequired"
                                  id="signatureNotRequired"
                                  innerRef={setInuputRefs}
                                  onKeyDown={(e) => handleKeyDown(e, formik)}
                                  value={false}
                                  checked={values.signRequired == false}
                                  onChange={() => setFieldValue("signRequired", false)}
                                />
                              </div>
                              <label className="form-check-label" htmlFor="signatureNotRequired">
                                No, Not Required
                                <span>
                                  Authority to leave parcel.
                                  <br />
                                  Add 'Delivery Instructions' in the field above.
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                        <RequiredFieldErrorMessage ref={childRequiredRef} />
                        <DeliveryInstructionReuired ref={childSignRef} />
                      </div>
                      <div className="saveAddress-btn">
                        <div className="button-effect">
                          <button
                            type="submit"
                            innerRef={setInuputRefs}
                            className="btn btn-products theme-primary"
                            onClick={() => checkRequiredField(isValid, errors)}
                            ref={setInuputRefs}
                          >
                            Save Address
                          </button>
                        </div>
                        <CancelButton
                          onClick={() => {
                            navigate(-1);
                          }}
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              )}
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};
